import { Spacer } from "@/components/Spacer";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import AppStoreLogo from "@/assets/images/appstore.png";
import PlayStoreLogo from "@/assets/images/playstore.png";
import HeroBg from "@/assets/images/hero-bg.png";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { motion, useInView, Variants } from "framer-motion";
import { memo } from "react";
import { trackAppDownload } from "@/utils/analytics";

export default function Hero({ isMobile }: { isMobile: boolean }) {
  const titleRef = useRef(null);
  const appStoreRef = useRef(null);
  const isInView = useInView(titleRef, { once: true, amount: 0.5 });
  const isAppStoreInView = useInView(appStoreRef, { once: true, amount: 0.5 });

  // useMemo로 lottie 소스 경로 메모이제이션
  const lottieSource = useMemo(
    () =>
      isMobile
        ? "/assets/lottie/hero-mobile.lottie"
        : "/assets/lottie/hero-pc.lottie",
    [isMobile]
  );

  // 애니메이션 variants 메모이제이션
  const titleVariants: Variants = useMemo(
    () => ({
      hidden: {
        opacity: 0,
        y: 20,
      },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          type: "spring",
          stiffness: 350,
          damping: 100,
          mass: 1,
          delay: 0.25,
        },
      },
      exit: {
        opacity: 0,
        y: 20,
        transition: {
          type: "spring",
          stiffness: 350,
          damping: 100,
          mass: 1,
          delay: 0.25,
        },
      },
    }),
    []
  );

  const appStoreVariants: Variants = useMemo(
    () => ({
      hidden: {
        opacity: 0,
        y: 20,
      },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 100,
          mass: 1,
          delay: 0.35,
        },
      },
      exit: {
        opacity: 0,
        y: 20,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 100,
          mass: 1,
          delay: 0.35,
        },
      },
    }),
    []
  );

  // 배경 스타일 메모이제이션
  const backgroundStyle = useMemo(
    () => ({
      backgroundImage: `url(${HeroBg})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    }),
    []
  );

  return (
    <div className="flex flex-col items-center" style={backgroundStyle}>
      <Spacer height={isMobile ? 112 : 125} />
      <motion.div
        ref={titleRef}
        id="title"
        className="font-pre-bold desktop:text-[48px] text-[26px] leading-[1.3em] whitespace-pre-line text-center"
        style={{ letterSpacing: "-0.04em" }}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        exit="exit"
        variants={titleVariants}
      >
        아플 때 가장 먼저 만나는{"\n"}닥터 AI 헬시어
      </motion.div>
      <Spacer height={isMobile ? 24 : 32} />
      <motion.div
        ref={appStoreRef}
        id="app-store-container"
        className="flex items-center gap-[12px]"
        initial="hidden"
        animate={isAppStoreInView ? "visible" : "hidden"}
        exit="exit"
        variants={appStoreVariants}
      >
        <PlayStore />
        <AppStore />
      </motion.div>
      <Spacer height={isMobile ? 20 : 8} />
      <div
        id="lottie-container"
        className="desktop:w-[960px] desktop:h-[509px] w-[350px] h-[380px]"
      >
        <DotLottieReact src={lottieSource} autoplay loop />
      </div>
    </div>
  );
}

// 자식 컴포넌트 메모이제이션
const AppStore = memo(() => {
  return (
    <div
      onClick={() => {
        // GA 이벤트 기록
        trackAppDownload("app_store");

        window.open(
          "https://apps.apple.com/kr/app/%ED%97%AC%EC%8B%9C%EC%96%B4-%EC%95%84%ED%94%8C-%EB%95%8C-%EA%B0%80%EC%9E%A5-%EB%A8%BC%EC%A0%80-%EB%A7%8C%EB%82%98%EB%8A%94-%EB%8B%A5%ED%84%B0ai/id6471542545",
          "_blank"
        );
      }}
      className="flex justify-left items-center desktop:gap-[8px] gap-[4px] desktop:pl-[12px] pl-[10px] desktop:pr-[14px] pr-[8px] desktop:py-[12px] py-[8px] bg-[#ffffff] rounded-[10px] cursor-pointer"
    >
      <img
        src={AppStoreLogo}
        alt="AppStore"
        className="desktop:w-[28px] desktop:h-[28px] w-[24px] h-[24px]"
        loading="lazy"
      />
      <div className="font-pre-bold desktop:text-[18px] text-[14px] leading-[1.3em] text-[#000000]">
        App Store
      </div>
    </div>
  );
});

AppStore.displayName = "AppStore";

const PlayStore = memo(() => {
  return (
    <div
      onClick={() => {
        // GA 이벤트 기록
        trackAppDownload("play_store");

        window.open(
          "https://play.google.com/store/apps/details?id=com.healthier&hl=ko",
          "_blank"
        );
      }}
      className="flex justify-left items-center desktop:gap-[8px] gap-[4px] desktop:pl-[12px] pl-[10px] desktop:pr-[14px] pr-[8px] desktop:py-[12px] py-[8px] bg-[#2E323A] rounded-[10px] cursor-pointer"
    >
      <img
        src={PlayStoreLogo}
        alt="PlayStore"
        className="desktop:w-[28px] desktop:h-[28px] w-[24px] h-[24px]"
        loading="lazy"
      />
      <div className="font-pre-bold desktop:text-[18px] text-[14px] leading-[1.3em] text-[#ffffff]">
        Google Play
      </div>
    </div>
  );
});

PlayStore.displayName = "PlayStore";
